import { z } from "zod";

// =============================================================================
// Error messages are keys of Connect.form.errors, translated where they are
// displayed (form fields on the client, toast messages from the server).
// =============================================================================

export const CONTACT_LIMITS = {
  name: 80,
  email: 254,
  subject: 150,
  messageMin: 10,
  message: 5000,
} as const;

export const contactSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, { error: "firstNameRequired" })
    .max(CONTACT_LIMITS.name, { error: "tooLong" }),
  lastName: z
    .string()
    .trim()
    .min(1, { error: "lastNameRequired" })
    .max(CONTACT_LIMITS.name, { error: "tooLong" }),
  email: z
    .string()
    .trim()
    .min(1, { error: "emailRequired" })
    .max(CONTACT_LIMITS.email, { error: "tooLong" })
    .pipe(z.email({ error: "emailInvalid" })),
  subject: z
    .string()
    .trim()
    .min(1, { error: "subjectRequired" })
    .max(CONTACT_LIMITS.subject, { error: "tooLong" }),
  message: z
    .string()
    .trim()
    .min(1, { error: "messageRequired" })
    .min(CONTACT_LIMITS.messageMin, { error: "messageTooShort" })
    .max(CONTACT_LIMITS.message, { error: "tooLong" }),
  /** Honeypot: hidden from humans, bots fill it */
  website: z.string().optional(),
});

export type ContactSchemaInput = z.input<typeof contactSchema>;
export type ContactSchema = z.output<typeof contactSchema>;

const FIELD_ERROR_KEYS = [
  "firstNameRequired",
  "lastNameRequired",
  "emailRequired",
  "emailInvalid",
  "subjectRequired",
  "messageRequired",
  "messageTooShort",
  "tooLong",
] as const;

export type ContactErrorKey =
  | (typeof FIELD_ERROR_KEYS)[number]
  | "invalid"
  | "rateLimited"
  | "generic";

/**
 * Key of Connect.form.errors matching a validation message ("invalid" for an
 * unexpected one).
 */
export function toContactErrorKey(message: string): ContactErrorKey {
  return (FIELD_ERROR_KEYS as readonly string[]).includes(message)
    ? (message as ContactErrorKey)
    : "invalid";
}

// =============================================================================
